// # 软件版本发布与编辑
<template>
  <div class="version-manage">
    <div class="header">
      <h2>版本管理</h2>
      <el-button type="primary" @click="showAddDialog">添加版本</el-button>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input
        v-model="searchQuery"
        placeholder="搜索版本号"
        class="search-input"
        clearable
        @clear="handleSearch"
        @input="handleSearch"
      />
      <el-select v-model="softwareFilter" placeholder="软件名称" clearable @change="handleSearch">
        <el-option label="全部" value="" />
        <el-option 
          v-for="name in uniqueSoftwareNames" 
          :key="name" 
          :label="name" 
          :value="name" 
        />
      </el-select>
      <el-select v-model="typeFilter" placeholder="下载方式" @change="handleSearch">
        <el-option label="全部" value="" />
        <el-option label="文件上传" value="upload" />
        <el-option label="外部链接" value="url" />
      </el-select>
      <el-button type="primary" @click="handleSearch">查询</el-button>
    </div>

    <!-- 版本列表 -->
    <el-table :data="filteredVersions" style="width: 100%" v-loading="loading">
      <el-table-column prop="software_name" label="软件名称" width="150" />
      <el-table-column prop="version_number" label="版本号" width="120" />
      <el-table-column prop="release_date" label="发布日期" width="120">
        <template #default="{ row }">
          {{ new Date(row.release_date).toLocaleDateString() }}
        </template>
      </el-table-column>
      <el-table-column prop="update_log" label="更新说明" show-overflow-tooltip />
      <el-table-column label="下载方式" width="100">
        <template #default="{ row }">
          <el-tag :type="row.file_type === 'upload' ? 'success' : 'info'">
            {{ row.file_type === 'upload' ? '文件上传' : '外部链接' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="下载地址" show-overflow-tooltip>
        <template #default="{ row }">
          <el-link 
            :href="row.file_type === 'upload' ? `/api/uploads/versions/${row.file_path}` : row.file_path" 
            target="_blank"
            type="primary"
          >
            {{ row.file_type === 'upload' ? row.file_path : row.file_path }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200">
        <template #default="{ row }">
          <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
          <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 添加/编辑对话框 -->
    <el-dialog 
      :title="dialogType === 'add' ? '添加版本' : '编辑版本'" 
      v-model="dialogVisible"
      width="50%"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="软件名称" prop="software_name">
          <el-input v-model="form.software_name" placeholder="例如：GAC_" />
        </el-form-item>
        <el-form-item label="版本号" prop="version_number">
          <el-input v-model="form.version_number" placeholder="例如：1.0.0" />
        </el-form-item>
        <el-form-item label="发布日期" prop="release_date">
          <el-date-picker
            v-model="form.release_date"
            type="date"
            placeholder="选择发布日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>
        <el-form-item label="更新说明" prop="update_log">
          <el-input type="textarea" v-model="form.update_log" rows="4" />
        </el-form-item>
        <el-form-item label="下载方式" prop="download_type">
          <el-radio-group v-model="form.download_type">
            <el-radio :value="'upload'">文件上传</el-radio>
            <el-radio :value="'url'">外部链接</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item 
          :label="form.download_type === 'upload' ? '上传文件' : '下载链接'" 
          :prop="form.download_type === 'upload' ? 'file' : 'download_link'"
        >
          <template v-if="form.download_type === 'upload'">
            <el-upload
              class="upload-demo"
              :action="`/api/admin/content/versions`"
              :headers="uploadHeaders"
              :on-success="handleUploadSuccess"
              :on-error="handleUploadError"
              :before-upload="beforeUpload"
              :limit="1"
              :file-list="fileList"
              :auto-upload="false"
              :on-change="handleFileChange"
              ref="uploadRef"
              name="file"
            >
              <template #trigger>
                <el-button type="primary">选择文件</el-button>
              </template>
              <template #tip>
                <div class="el-upload__tip">
                  支持任意格式文件，建议不超过1.5GB
                </div>
              </template>
            </el-upload>
          </template>
          <template v-else>
            <el-input v-model="form.download_link" placeholder="输入下载链接" />
          </template>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          确定
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '@/utils/request'

export default {
  name: 'VersionManage',
  setup() {
    const versions = ref([])
    const loading = ref(false)
    const dialogVisible = ref(false)
    const dialogType = ref('add')
    const submitting = ref(false)
    const formRef = ref(null)
    const currentId = ref(null)
    const fileList = ref([])
    const uploadRef = ref(null)
    const currentFile = ref(null)

    const form = ref({
      software_name: '',
      version_number: '',
      release_date: '',
      update_log: '',
      download_type: 'upload',
      download_link: '',
      file: null
    })

    const rules = {
      software_name: [{ required: true, message: '请输入软件名称', trigger: 'blur' }],
      version_number: [
        { required: true, message: '请输入版本号', trigger: 'blur' },
        { pattern: /^\d+\.\d+\.\d+$/, message: '版本号格式应为 x.x.x', trigger: 'blur' }
      ],
      release_date: [{ required: true, message: '请选择发布日期', trigger: 'change' }],
      update_log: [{ required: true, message: '请输入更新说明', trigger: 'blur' }],
      download_type: [{ required: true, message: '请选择下载方式', trigger: 'change' }],
      file: [{ required: true, message: '请上传文件', trigger: 'change' }],
      download_link: [{ required: true, message: '请输入下载链接', trigger: 'blur' }]
    }

    // 搜索和筛选
    const searchQuery = ref('')
    const typeFilter = ref('')
    const softwareFilter = ref('')

    // 分页相关
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)

    // 获取唯一的软件名称列表
    const uniqueSoftwareNames = computed(() => {
      const names = versions.value.map(v => v.software_name).filter(name => name)
      return [...new Set(names)]
    })

    // 过滤后的版本列表
    const filteredVersions = computed(() => {
      let result = versions.value

      // 搜索版本号
      if (searchQuery.value) {
        result = result.filter(v => 
          v.version_number.toLowerCase().includes(searchQuery.value.toLowerCase())
        )
      }

      // 筛选软件名称
      if (softwareFilter.value) {
        result = result.filter(v => v.software_name === softwareFilter.value)
      }

      // 筛选下载方式
      if (typeFilter.value) {
        result = result.filter(v => v.file_type === typeFilter.value)
      }

      return result
    })

    // 获取版本列表
    const fetchVersions = async () => {
      loading.value = true
      try {
        const res = await request.get('/admin/content/versions')
        console.log('获取到的版本数据:', res) // 调试日志
        if (Array.isArray(res)) {
          versions.value = res
          total.value = res.length
        } else if (res.data && Array.isArray(res.data)) {
          versions.value = res.data
          total.value = res.data.length
        } else {
          console.error('返回的数据格式不正确:', res)
          ElMessage.error('数据格式错误')
        }
      } catch (error) {
        ElMessage.error('获取版本列表失败')
        console.error('获取版本列表失败:', error)
      } finally {
        loading.value = false
      }
    }

    // 显示添加对话框
    const showAddDialog = () => {
      dialogType.value = 'add'
      form.value = {
        software_name: '',
        version_number: '',
        release_date: '',
        update_log: '',
        download_type: 'upload',
        download_link: '',
        file: null
      }
      fileList.value = []
      currentFile.value = null
      dialogVisible.value = true
    }

    // 处理编辑
    const handleEdit = (row) => {
      dialogType.value = 'edit'
      currentId.value = row.id
      form.value = {
        software_name: row.software_name,
        version_number: row.version_number,
        release_date: row.release_date,
        update_log: row.update_log,
        download_type: row.file_type === 'upload' ? 'upload' : 'url',
        download_link: row.file_type === 'url' ? row.file_path : '',
        file: null
      }
      if (row.file_type === 'upload') {
        fileList.value = [{
          name: row.file_path,
          url: `/api/uploads/versions/${row.file_path}`
        }]
      } else {
        fileList.value = []
      }
      currentFile.value = null
      dialogVisible.value = true
    }

    // 处理删除
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确定要删除这个版本吗？', '提示', {
          type: 'warning'
        })
        await request.delete(`/admin/content/versions/${row.id}`)
        ElMessage.success('删除成功')
        fetchVersions()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }

    // 提交表单
    const handleSubmit = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        submitting.value = true
        
        const formData = new FormData()
        
        // 必填字段验证
        if (!form.value.software_name) {
          throw new Error('请输入软件名称')
        }
        if (!form.value.version_number) {
          throw new Error('请输入版本号')
        }
        if (!form.value.release_date) {
          throw new Error('请选择发布日期')
        }
        if (!form.value.update_log) {
          throw new Error('请输入更新说明')
        }
        
        // 添加基本字段
        formData.append('software_name', form.value.software_name)
        formData.append('version_number', form.value.version_number)
        formData.append('release_date', form.value.release_date)
        formData.append('update_log', form.value.update_log)
        formData.append('admin_id', localStorage.getItem('adminId'))

        // 处理下载方式
        if (form.value.download_type === 'upload') {
          if (!currentFile.value) {
            throw new Error('请选择文件')
          }
          formData.append('file', currentFile.value)
        } else {
          if (!form.value.download_link) {
            throw new Error('请输入下载链接')
          }
          formData.append('download_link', form.value.download_link)
        }
        
        console.log('准备提交的表单数据:', {
          software_name: form.value.software_name,
          version_number: form.value.version_number,
          release_date: form.value.release_date,
          update_log: form.value.update_log,
          download_type: form.value.download_type,
          file: currentFile.value,
          download_link: form.value.download_link
        })
        
        try {
          if (dialogType.value === 'add') {
            console.log('开始添加版本...')
            const response = await request.post('/admin/content/versions', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            })
            console.log('添加版本响应:', response)
            ElMessage.success('添加成功')
          } else {
            console.log('开始更新版本...')
            const response = await request.put(`/admin/content/versions/${currentId.value}`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            })
            console.log('更新版本响应:', response)
            ElMessage.success('更新成功')
          }
          
          dialogVisible.value = false
          fetchVersions()
        } catch (submitError) {
          console.error('提交请求错误:', submitError)
          if (submitError.response) {
            console.error('错误响应数据:', submitError.response.data)
            throw new Error(`提交失败: ${submitError.response.data.message || submitError.response.data.error || '未知错误'}`)
          } else if (submitError.request) {
            throw new Error('服务器无响应，请检查网络连接')
          } else {
            throw new Error(`提交失败: ${submitError.message || '未知错误'}`)
          }
        }
      } catch (error) {
        console.error('表单提交失败:', error)
        ElMessage.error(error.message || (dialogType.value === 'add' ? '添加失败' : '更新失败'))
      } finally {
        submitting.value = false
      }
    }

    // 上传相关
    const uploadHeaders = computed(() => ({
      Authorization: `Bearer ${localStorage.getItem('adminToken')}`
    }))

    const beforeUpload = (file) => {
      console.log('准备上传文件:', file)
      const isLt200M = file.size / 1024 / 1024 < 200
      if (!isLt200M) {
        ElMessage.error('文件大小不能超过 200MB!')
        return false
      }
      currentFile.value = file
      return false // 阻止自动上传
    }

    const handleFileChange = (file) => {
      console.log('文件已选择:', file)
      if (file && file.raw) {
        currentFile.value = file.raw
        form.value.file = file.raw
        console.log('文件已更新到表单:', file.raw)
      }
    }

    const handleUploadSuccess = (response) => {
      console.log('上传成功响应:', response)
      if (response && response.filename) {
        form.value.file = response.filename
        ElMessage.success('上传成功')
        return true
      } else {
        console.error('上传响应格式错误:', response)
        ElMessage.error('上传响应格式错误')
        return false
      }
    }

    const handleUploadError = (error) => {
      console.error('上传失败:', error)
      if (error.response) {
        console.error('错误响应数据:', error.response.data)
        ElMessage.error(error.response.data.message || error.response.data.error || '上传失败')
      } else {
        ElMessage.error(error.message || '上传失败')
      }
      return false
    }

    // 搜索
    const handleSearch = () => {
      currentPage.value = 1
      fetchVersions()
    }

    // 分页大小改变
    const handleSizeChange = (val) => {
      pageSize.value = val
      fetchVersions()
    }

    // 页码改变
    const handleCurrentChange = (val) => {
      currentPage.value = val
      fetchVersions()
    }

    onMounted(() => {
      fetchVersions()
    })

    return {
      versions,
      loading,
      dialogVisible,
      dialogType,
      submitting,
      form,
      formRef,
      rules,
      fileList,
      uploadHeaders,
      searchQuery,
      typeFilter,
      softwareFilter,
      uniqueSoftwareNames,
      filteredVersions,
      showAddDialog,
      handleEdit,
      handleDelete,
      handleSubmit,
      handleUploadSuccess,
      handleUploadError,
      beforeUpload,
      handleFileChange,
      currentPage,
      pageSize,
      total,
      handleSearch,
      handleSizeChange,
      handleCurrentChange
    }
  }
}
</script>

<style scoped>
.version-manage {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-bar {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.search-input {
  width: 300px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.el-upload__tip {
  color: #909399;
  font-size: 12px;
  margin-top: 7px;
}
</style>